import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Package, MessageSquare, Truck, Heart, ClipboardList, BadgeCheck, CreditCard, Gavel } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ listings: 0, active: 0, featured: 0 });

  useEffect(() => {
    api.get("/listings/mine").then(({ data }) => {
      const items = data.items || [];
      setStats({
        listings: items.length,
        active: items.filter((x) => x.status === "active").length,
        featured: items.filter((x) => x.is_featured).length,
      });
    });
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <div className="overline text-safety">Dashboard</div>
      <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter text-forest">Hi {user?.name?.split(" ")[0]} 👋</h1>
      <p className="mt-3 text-muted-foreground">Your marketplace at a glance.</p>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat n={stats.listings} l="Total listings" />
        <Stat n={stats.active} l="Active" />
        <Stat n={stats.featured} l="Featured" />
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <ActionCard
          testId="dash-post-listing"
          to="/sell"
          icon={<Plus className="w-5 h-5" />}
          title="Post a new listing"
          body="Sell a desk or clear an entire office in under 2 minutes."
        />
        <ActionCard
          testId="dash-my-listings"
          to="/dashboard/listings"
          icon={<Package className="w-5 h-5" />}
          title="Manage listings"
          body="Edit, feature or remove anything you've listed."
        />
        <ActionCard
          testId="dash-inbox"
          to="/dashboard/messages"
          icon={<MessageSquare className="w-5 h-5" />}
          title="Inbox"
          body="Chat with buyers, sellers and removal firms."
        />
        <ActionCard
          testId="dash-saved"
          to="/dashboard/saved"
          icon={<Heart className="w-5 h-5" />}
          title="Saved listings"
          body="Every item you've hearted, in one place."
        />
        <ActionCard
          testId="dash-bidding"
          to="/dashboard/bidding"
          icon={<Gavel className="w-5 h-5" />}
          title="Dealer bidding"
          body="Place bids, track offers and manage your Bidding Access subscription."
        />
        <ActionCard
          testId="dash-briefs"
          to="/dashboard/briefs"
          icon={<ClipboardList className="w-5 h-5" />}
          title="My clearance requests"
          body="Post clearance requests and see removal-firm quotes."
        />
        <ActionCard
          testId="dash-browse-briefs"
          to="/briefs"
          icon={<Truck className="w-5 h-5" />}
          title="Browse open requests"
          body="Removal firms — quote on live clearance jobs."
        />
        {user?.user_type === "removal_company" && (
          <ActionCard
            testId="dash-company"
            to="/dashboard/company"
            icon={<Truck className="w-5 h-5" />}
            title="My company"
            body="Manage services, service areas & see incoming clearance requests."
          />
        )}
        {(user?.user_type === "business" || user?.user_type === "removal_company") && (
          <ActionCard
            testId="dash-payouts"
            to="/dashboard/payouts"
            icon={<CreditCard className="w-5 h-5" />}
            title="Payouts (Stripe)"
            body="Connect Stripe Express to receive payouts & verify your business."
          />
        )}
        {(user?.user_type === "business" || user?.user_type === "removal_company") && (
          <ActionCard
            testId="dash-verification"
            to="/dashboard/verification"
            icon={<BadgeCheck className="w-5 h-5" />}
            title={user?.is_verified ? "Verified ✓" : "Get verified"}
            body={user?.is_verified ? "Your Verified badge is live on all listings & profile." : "Upload proof of insurance to earn a Verified badge."}
          />
        )}
      </div>

      <div className="mt-10 border border-border bg-white p-8 lg:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
        <div>
          <div className="overline text-safety">Need it moved?</div>
          <div className="mt-1 font-display text-2xl tracking-tight text-forest">Browse commercial removal firms</div>
          <p className="mt-2 text-sm text-muted-foreground">Vetted UK-wide movers ready to quote.</p>
        </div>
        <Button asChild className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8" data-testid="dash-find-movers">
          <Link to="/removal-companies"><Truck className="w-4 h-4 mr-2" /> Find movers</Link>
        </Button>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border border-border bg-white p-6">
      <div className="font-display font-black text-4xl text-forest tabular-nums">{n}</div>
      <div className="overline text-muted-foreground mt-1">{l}</div>
    </div>
  );
}

function ActionCard({ to, icon, title, body, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group border border-border bg-white p-6 block transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="w-10 h-10 bg-forest text-white grid place-items-center">{icon}</div>
      <div className="mt-4 font-display text-xl tracking-tight group-hover:text-forest">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </Link>
  );
}
